import React, { useEffect, useState } from "react";
import {
    Loader2,
    RefreshCw,
    Search,
    User as UserIcon,
    Mail,
    Phone,
    MapPin,
    Euro,
    Package,
    X,
    Calendar,
    Home,
    UserCheck,
    UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import {
    ORDER_STATUS_LABELS,
    ORDER_STATUS_STYLES,
    formatDateTime,
    formatPrice,
} from "../lib/orderStatus";
import { LEAD_STATUS_LABELS, LEAD_STATUS_STYLES } from "../lib/leadStatus";

export default function AdminCustomers({ apiAuth }) {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(true);
    const [q, setQ] = useState("");
    const [openId, setOpenId] = useState(null);

    const load = async () => {
        setBusy(true);
        try {
            const params = q ? `?q=${encodeURIComponent(q)}` : "";
            const { data } = await apiAuth(`/admin/customers${params}`);
            setRows(data || []);
        } catch {
            toast.error("Erro ao carregar clientes");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitSearch = (e) => {
        e.preventDefault();
        load();
    };

    const registered = rows.filter((r) => r.kind === "registered").length;
    const guests = rows.filter((r) => r.kind === "guest").length;
    const totalSpent = rows.reduce((a, b) => a + (b.total_spent || 0), 0);

    return (
        <div className="space-y-6" data-testid="admin-customers-panel">
            {/* Stats */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MiniCard
                    icon={UserCheck}
                    label="Registados"
                    value={registered}
                    accent="text-brand-green"
                    bg="bg-white"
                    testid="customers-registered"
                />
                <MiniCard
                    icon={UserPlus}
                    label="Convidados"
                    value={guests}
                    subtitle="checkout sem conta"
                    accent="text-brand-orange"
                    bg="bg-brand-orange-soft"
                    testid="customers-guests"
                />
                <MiniCard
                    icon={UserIcon}
                    label="Total"
                    value={rows.length}
                    accent="text-white"
                    bg="bg-brand-ink"
                    white
                    testid="customers-total"
                />
                <MiniCard
                    icon={Euro}
                    label="Vendas acumuladas"
                    value={formatPrice(totalSpent)}
                    accent="text-white"
                    bg="bg-brand-green"
                    white
                    testid="customers-revenue"
                />
            </section>

            {/* Search bar */}
            <form
                onSubmit={submitSearch}
                className="flex items-center gap-2 flex-wrap"
            >
                <div className="relative flex-1 min-w-[280px]">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink-soft" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Procurar por nome ou email..."
                        data-testid="customers-search-input"
                        className="w-full rounded-full bg-white border-2 border-brand-green/15 focus:border-brand-green pl-11 pr-4 py-2.5 text-sm font-medium text-brand-ink placeholder:text-brand-ink-soft focus:outline-none"
                    />
                </div>
                <button
                    type="submit"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink hover:bg-[#2C312E] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
                    data-testid="customers-search-submit"
                >
                    Procurar
                </button>
                <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors"
                    data-testid="customers-refresh"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                    Atualizar
                </button>
            </form>

            {/* Table */}
            <section className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden">
                <div className="px-6 py-4 border-b border-brand-green/10">
                    <h2 className="font-display font-medium text-lg text-brand-ink">
                        Clientes ({rows.length})
                    </h2>
                </div>
                {busy ? (
                    <div className="p-16 flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-16 text-center">
                        <UserIcon className="h-10 w-10 text-brand-green/40 mx-auto" />
                        <p className="mt-4 text-sm text-brand-ink-soft">
                            Ainda não há clientes.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-brand-cream/60 text-[11px] uppercase tracking-widest text-brand-ink-soft">
                                <tr>
                                    <th className="text-left px-6 py-3 font-bold">Cliente</th>
                                    <th className="text-left px-6 py-3 font-bold hidden md:table-cell">
                                        Contactos
                                    </th>
                                    <th className="text-right px-6 py-3 font-bold">Encomendas</th>
                                    <th className="text-right px-6 py-3 font-bold">Total gasto</th>
                                    <th className="text-left px-6 py-3 font-bold hidden lg:table-cell">
                                        Última
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-brand-green/10">
                                {rows.map((c) => (
                                    <tr
                                        key={c.id}
                                        onClick={() => setOpenId(c.id)}
                                        data-testid={`customer-row-${c.id}`}
                                        className="hover:bg-brand-cream/40 cursor-pointer transition-colors"
                                    >
                                        <td className="px-6 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="font-bold text-brand-ink truncate">
                                                    {c.name || "(sem nome)"}
                                                </div>
                                                {c.kind === "guest" && (
                                                    <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-brand-orange-soft text-brand-orange">
                                                        Guest
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-brand-ink-soft truncate">
                                                {c.email}
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 hidden md:table-cell text-xs text-brand-ink-soft">
                                            {c.phone || "—"}
                                        </td>
                                        <td className="px-6 py-3 text-right font-semibold text-brand-ink">
                                            {c.orders_count}
                                        </td>
                                        <td className="px-6 py-3 text-right font-bold text-brand-green">
                                            {formatPrice(c.total_spent || 0)}
                                        </td>
                                        <td className="px-6 py-3 text-xs text-brand-ink-soft hidden lg:table-cell">
                                            {c.last_order_at ? formatDateTime(c.last_order_at) : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {openId && (
                <CustomerDrawer
                    apiAuth={apiAuth}
                    customerId={openId}
                    onClose={() => setOpenId(null)}
                />
            )}
        </div>
    );
}

const MiniCard = ({ icon: Icon, label, value, subtitle, accent, bg, white, testid }) => (
    <div
        data-testid={testid}
        className={`rounded-2xl ${bg} p-5 shadow-soft ring-1 ring-brand-green/5`}
    >
        <Icon className={`h-5 w-5 ${accent}`} />
        <div
            className={`mt-3 text-[11px] uppercase tracking-widest font-bold ${
                white ? "text-white/70" : "text-brand-ink-soft"
            }`}
        >
            {label}
        </div>
        <div
            className={`mt-1 font-display font-medium text-2xl md:text-3xl ${
                white ? "text-white" : "text-brand-ink"
            }`}
        >
            {value}
        </div>
        {subtitle && (
            <div className={`mt-1 text-xs ${white ? "text-white/70" : "text-brand-ink-soft"}`}>
                {subtitle}
            </div>
        )}
    </div>
);

const CustomerDrawer = ({ apiAuth, customerId, onClose }) => {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setBusy(true);
            try {
                const { data } = await apiAuth(
                    `/admin/customers/${encodeURIComponent(customerId)}`,
                );
                if (mounted) setData(data);
            } catch {
                toast.error("Erro ao carregar cliente");
                onClose();
            } finally {
                if (mounted) setBusy(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [customerId, apiAuth, onClose]);

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-stretch justify-end"
            onClick={onClose}
            data-testid="customer-drawer"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl bg-brand-cream h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-200"
            >
                <div className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-brand-green/10 px-6 py-4 flex items-center justify-between z-10">
                    <h3 className="font-display font-medium text-lg text-brand-ink">
                        Detalhes do cliente
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-brand-cream transition-colors"
                        data-testid="customer-drawer-close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-6 space-y-6">
                    {busy || !data ? (
                        <div className="py-16 flex justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                        </div>
                    ) : (
                        <>
                            {/* Identity */}
                            <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-full bg-brand-green-soft flex items-center justify-center">
                                        <UserIcon className="h-6 w-6 text-brand-green" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display font-medium text-xl text-brand-ink truncate">
                                            {data.name || "(sem nome)"}
                                        </div>
                                        <div className="text-xs text-brand-ink-soft">
                                            {data.kind === "registered"
                                                ? "Cliente registado"
                                                : "Cliente convidado"}
                                            {data.created_at &&
                                                ` · desde ${formatDateTime(data.created_at)}`}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-4 text-sm text-brand-ink-soft">
                                    {data.email && (
                                        <a
                                            href={`mailto:${data.email}`}
                                            className="inline-flex items-center gap-1.5 hover:text-brand-green"
                                        >
                                            <Mail className="h-3.5 w-3.5" /> {data.email}
                                        </a>
                                    )}
                                    {data.phone && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Phone className="h-3.5 w-3.5" /> {data.phone}
                                        </span>
                                    )}
                                </div>
                                {/* Stats */}
                                <div className="mt-4 grid grid-cols-3 gap-3">
                                    <div className="rounded-xl bg-brand-cream p-3 text-center">
                                        <div className="text-[10px] uppercase font-bold text-brand-ink-soft">
                                            Encomendas
                                        </div>
                                        <div className="mt-1 font-bold text-brand-ink">
                                            {data.stats?.orders_count ?? 0}
                                        </div>
                                    </div>
                                    <div className="rounded-xl bg-brand-green-soft p-3 text-center">
                                        <div className="text-[10px] uppercase font-bold text-brand-green">
                                            Total gasto
                                        </div>
                                        <div className="mt-1 font-bold text-brand-green">
                                            {formatPrice(data.stats?.total_spent || 0)}
                                        </div>
                                    </div>
                                    <div className="rounded-xl bg-brand-orange-soft p-3 text-center">
                                        <div className="text-[10px] uppercase font-bold text-brand-orange">
                                            Ticket médio
                                        </div>
                                        <div className="mt-1 font-bold text-brand-orange">
                                            {formatPrice(data.stats?.avg_ticket || 0)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Addresses */}
                            {data.addresses && data.addresses.length > 0 && (
                                <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-green">
                                        <Home className="h-3.5 w-3.5" /> Moradas
                                    </div>
                                    <ul className="mt-3 space-y-3">
                                        {data.addresses.map((a) => (
                                            <li
                                                key={a.id}
                                                className="rounded-xl bg-brand-cream/70 p-3 text-sm"
                                            >
                                                <div className="font-semibold text-brand-ink">
                                                    {a.full_name || data.name}
                                                    {a.is_default && (
                                                        <span className="ml-2 text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 bg-brand-green text-white">
                                                            Padrão
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-brand-ink-soft mt-1">
                                                    {a.line1}
                                                    {a.line2 && `, ${a.line2}`}
                                                    <br />
                                                    {a.postal_code} {a.city}, {a.country}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Orders */}
                            <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-green">
                                    <Package className="h-3.5 w-3.5" /> Encomendas ({data.orders?.length ?? 0})
                                </div>
                                {(!data.orders || data.orders.length === 0) ? (
                                    <div className="mt-4 text-center text-sm text-brand-ink-soft">
                                        Sem encomendas ainda.
                                    </div>
                                ) : (
                                    <ul className="mt-3 divide-y divide-brand-green/10">
                                        {data.orders.map((o) => (
                                            <li key={o.id} className="py-3 flex items-center gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-semibold text-brand-ink">
                                                        #{o.order_number || o.id?.slice(0, 8)}
                                                    </div>
                                                    <div className="text-xs text-brand-ink-soft">
                                                        {formatDateTime(o.created_at)}
                                                        {o.items_snapshot?.length > 0 && (
                                                            <>
                                                                {" · "}
                                                                {o.items_snapshot
                                                                    .map(
                                                                        (it) =>
                                                                            `${it.name} x${it.quantity}`,
                                                                    )
                                                                    .join(", ")}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <span
                                                    className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${
                                                        ORDER_STATUS_STYLES[o.order_status] ||
                                                        "bg-brand-cream text-brand-ink"
                                                    }`}
                                                >
                                                    {ORDER_STATUS_LABELS[o.order_status] ||
                                                        o.order_status}
                                                </span>
                                                <span className="text-sm font-bold text-brand-ink w-20 text-right">
                                                    {formatPrice(o.amount || 0)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Related leads */}
                            {data.leads && data.leads.length > 0 && (
                                <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-orange">
                                        <UserPlus className="h-3.5 w-3.5" /> Leads deste email ({data.leads.length})
                                    </div>
                                    <ul className="mt-3 divide-y divide-brand-green/10">
                                        {data.leads.map((l) => (
                                            <li key={l.id} className="py-2.5 flex items-center gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-semibold text-brand-ink truncate">
                                                        {l.interest === "parceiro"
                                                            ? "Parceiro"
                                                            : "Consumidor"}
                                                        {l.line_interest &&
                                                            ` · ${l.line_interest.toUpperCase()}`}
                                                    </div>
                                                    <div className="text-[11px] text-brand-ink-soft">
                                                        <Calendar className="inline h-3 w-3 mr-1" />
                                                        {formatDateTime(l.created_at)}
                                                    </div>
                                                </div>
                                                <span
                                                    className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${
                                                        LEAD_STATUS_STYLES[l.status] ||
                                                        "bg-brand-cream text-brand-ink"
                                                    }`}
                                                >
                                                    {LEAD_STATUS_LABELS[l.status] || l.status}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
